import { Controller, type Control } from 'react-hook-form';
import { Input, Label } from '@/shared/components';
import { SERVICE_SCHEDULE_LIMITS, type ServiceFormInput, type ServiceFormOutput } from '../../schemas/service.schema';

interface ServiceScheduleFieldsProps {
    control: Control<ServiceFormInput, unknown, ServiceFormOutput>;
    fieldId: (field: string) => string;
    disabled: boolean;
}

const POSITION_HINT = 'Leave blank to add at the end; entering a position moves later services down.';
const SERVICE_TYPE_HINT = 'Defaults to Service {position}.';

export function ServiceScheduleFields({ control, fieldId, disabled }: ServiceScheduleFieldsProps) {
    return (
        <>
            <div>
                <Label htmlFor={fieldId('position')}>Position <span className="text-mp-muted">(optional)</span></Label>
                <Controller
                    name="position"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId('position')}
                            type="number"
                            min="1"
                            max={String(SERVICE_SCHEDULE_LIMITS.position)}
                            placeholder="Next"
                            value={field.value?.toString() ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            error={!!error}
                            hint={error?.message ?? POSITION_HINT}
                            hintId={fieldId('position-hint')}
                            aria-invalid={!!error}
                            aria-describedby={fieldId('position-hint')}
                        />
                    )}
                />
            </div>
            <div>
                <Label htmlFor={fieldId('service_type')}>Service Type <span className="text-mp-muted">(optional)</span></Label>
                <Controller
                    name="service_type"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId('service_type')}
                            placeholder="Service 1"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            error={!!error}
                            hint={error?.message ?? SERVICE_TYPE_HINT}
                            hintId={fieldId('service_type-hint')}
                            aria-invalid={!!error}
                            aria-describedby={fieldId('service_type-hint')}
                        />
                    )}
                />
            </div>
            <div>
                <Label htmlFor={fieldId('months_from_start')} required>Months from start</Label>
                <Controller
                    name="months_from_start"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId('months_from_start')}
                            type="number"
                            min="1"
                            max={String(SERVICE_SCHEDULE_LIMITS.months_from_start)}
                            placeholder="e.g. 12"
                            value={field.value?.toString() ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            error={!!error}
                            hint={error?.message}
                            hintId={fieldId('months_from_start-hint')}
                            aria-required={true}
                            aria-invalid={!!error}
                            aria-describedby={error ? fieldId('months_from_start-hint') : undefined}
                        />
                    )}
                />
            </div>
            <div>
                <Label htmlFor={fieldId('mileage_from_start')} required>Mileage from start</Label>
                <Controller
                    name="mileage_from_start"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId('mileage_from_start')}
                            type="number"
                            min="1"
                            max={String(SERVICE_SCHEDULE_LIMITS.mileage_from_start)}
                            placeholder="e.g. 10000"
                            value={field.value?.toString() ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            error={!!error}
                            hint={error?.message}
                            hintId={fieldId('mileage_from_start-hint')}
                            aria-required={true}
                            aria-invalid={!!error}
                            aria-describedby={error ? fieldId('mileage_from_start-hint') : undefined}
                        />
                    )}
                />
            </div>
        </>
    );
}
